/* Deterministic pseudo-randomness for the mock data layer.
 *
 * Every figure the dashboard shows is derived from a seed hashed off the
 * active query (scope + window + filters). Same query → identical numbers,
 * always — so the UI is stable across renders, tests are not flaky, and two
 * different scopes still look meaningfully different. No Date.now / Math.random
 * anywhere in the derivation. */

/** Mulberry32 — a tiny, fast, well-distributed 32-bit PRNG. */
export function mulberry32(seed: number): () => number {
  let a = seed >>> 0;
  return function next() {
    a |= 0;
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** FNV-1a string hash → uint32. Stable across runs and platforms. */
export function hashString(str: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < str.length; i += 1) {
    h ^= str.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return h >>> 0;
}

/** A seeded random source with the small set of helpers the derivation needs. */
export class Rng {
  private next: () => number;

  constructor(seed: number | string) {
    this.next = mulberry32(typeof seed === "string" ? hashString(seed) : seed);
  }

  /** Uniform float in [0, 1). */
  float(): number {
    return this.next();
  }

  /** Uniform float in [min, max). */
  range(min: number, max: number): number {
    return min + this.next() * (max - min);
  }

  /** Integer in [min, max] inclusive. */
  int(min: number, max: number): number {
    return Math.floor(this.range(min, max + 1));
  }

  /** A multiplicative jitter around 1, e.g. jitter(0.2) ∈ [0.8, 1.2). */
  jitter(spread: number): number {
    return 1 + (this.next() * 2 - 1) * spread;
  }

  /** Pick one element. */
  pick<T>(items: readonly T[]): T {
    return items[Math.floor(this.next() * items.length)];
  }

  /** Approx. standard-normal via averaging (Irwin–Hall, n=4), mean 0, ~unit. */
  gaussian(): number {
    return (this.next() + this.next() + this.next() + this.next() - 2) * 1.0;
  }
}

/** Split `total` across `weights` proportionally, preserving the exact sum
 *  (largest-remainder rounding when `round` is set). Used so a breakdown's
 *  parts always add back up to its whole. */
export function splitByWeights(
  total: number,
  weights: number[],
  round = false,
): number[] {
  const sum = weights.reduce((a, b) => a + b, 0) || 1;
  const raw = weights.map((w) => (w / sum) * total);
  if (!round) return raw;

  const floors = raw.map(Math.floor);
  let remainder = Math.round(total) - floors.reduce((a, b) => a + b, 0);
  const order = raw
    .map((v, i) => ({ i, frac: v - Math.floor(v) }))
    .sort((a, b) => b.frac - a.frac);
  const out = floors.slice();
  for (let k = 0; k < order.length && remainder > 0; k += 1, remainder -= 1) {
    out[order[k].i] += 1;
  }
  return out;
}
